/**
 * Tome I Reader contents — same four-space/card language as the Owner's
 * approved ProductWorkspace. Only the existing access-gated Book API supplies
 * sections, categories and pages. No Editorial/admin API, invented sample
 * recipes, alternate Book collection or copy of the author's material.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getVolumeEntry, searchBook } from "@/lib/bookApi";
import { useAuth } from "@/context/AuthContext";
import { ALMOND_PUBLICATION_SPACES } from "@/lib/almondPublicationCatalogV1.mjs";
import { ArrowLeft, ArrowRight, BookOpen, CakeSlice, FlaskConical, Leaf, LibraryBig, Search } from "lucide-react";

import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import TomeIAlmondHero from "@/components/book/TomeIAlmondHero";

const SPACE_ICONS = [Leaf, LibraryBig, CakeSlice, FlaskConical];
const bg = "#F4EBDD";

// The same visual shell supports private Owner draft QA and buyer visibility.
// "Как читатель" only shows published records; root intro remains server-gated.
export function visibleBookSections(sections = [], ownerDraftPreview = false) {
  const visible = (item) => ownerDraftPreview || item.status === "published";
  return sections.filter(visible).map((section) => ({
    ...section,
    pages: (section.pages || []).filter(visible),
    subsections: (section.subsections || [])
      .filter(visible)
      .map((category) => ({
        ...category,
        pages: (category.pages || []).filter(visible),
      })),
  }));
}

export function visibleBookPageIds(sections = [], introPages = [], ownerDraftPreview = false) {
  const ids = (introPages || []).filter((p) => p.status === "published").map((p) => p.id);
  for (const sec of visibleBookSections(sections, ownerDraftPreview)) {
    ids.push(...sec.pages.map((p) => p.id));
    for (const sub of sec.subsections) ids.push(...sub.pages.map((p) => p.id));
  }
  return new Set(ids);
}

export default function AlmondBookContents({ slugOverride = null, embedded = false, searchQuery = "", onSearchQueryChange = null }) {
  const route = useParams();
  const slug = slugOverride || route.slug;
  const nav = useNavigate();
  const { user } = useAuth();
  const contentsRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [localQuery, setLocalQuery] = useState("");
  const ownerSearch = embedded && typeof onSearchQueryChange === "function";
  const query = ownerSearch ? searchQuery : localQuery;
  const setQuery = ownerSearch ? onSearchQueryChange : setLocalQuery;
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let active = true;
    setEntry(null);
    setError("");
    getVolumeEntry(slug).then((data) => {
      if (!active) return;
      if (!data.has_access && !data.is_editor) {
        setError("Для чтения Тома необходим доступ к продукту.");
        return;
      }
      setEntry(data);
      setActiveSpaceId(null);
      setActiveCategoryId(null);
    }).catch((err) => {
      if (!active) return;
      setError(err?.response?.status === 404 ? "Том не найден." : "Не удалось открыть содержание Тома.");
    });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!entry || !query.trim()) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    let active = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await searchBook(slug, query.trim());
        if (active) {
          const readable = visibleBookPageIds(entry.sections, entry.intro_pages,
            entry.is_editor && searchParams.get("asReader") !== "1");
          setSearchResults(Array.isArray(result) ? result.filter((p) => readable.has(p.id)) : []);
        }
      } catch {
        if (active) setSearchResults([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [query, entry, slug, searchParams]);

  const ownerDraftPreview = !!entry?.is_editor && searchParams.get("asReader") !== "1";
  const returnToOwner = !embedded && entry?.is_editor && user?.role === "owner"
    && searchParams.get("from") === "tome-owner";
  const ownerContentsPath = "/admin/products/78917116-d963-45dd-946c-7797db939790?tab=content";
  const sections = visibleBookSections(entry?.sections || [], ownerDraftPreview);
  const spaces = ALMOND_PUBLICATION_SPACES.map((canonical, i) => ({
    ...canonical,
    section: sections.find((section) => section.slug === canonical.key) || null,
    icon: SPACE_ICONS[i] || BookOpen,
  }));
  // Navigation is always openable, even before editorial content is released.
  // Category labels come from the approved publication catalogue; ONLY Book
  // sections/subsections/pages from the access-gated API supply readable pages.
  const activeSpace = spaces.find((space) => space.key === activeSpaceId) || spaces[0];
  const categories = activeSpace.categories.map((category) => ({
    ...category,
    bookCategory: activeSpace.section?.subsections?.find((sub) => sub.slug === category.id) || null,
  }));
  const activeCategory = categories.find((category) => category.id === activeCategoryId) || categories[0] || null;
  const directPages = activeSpace.section?.pages || [];
  const pages = activeCategory ? (activeCategory.bookCategory?.pages || []) : directPages;
  const openPage = (id) => nav(`/book/${encodeURIComponent(slug)}/pages/${encodeURIComponent(id)}${embedded || returnToOwner ? "?from=tome-owner" : ""}`);
  const openSpace = (spaceKey) => {
    setActiveSpaceId(spaceKey);
    setActiveCategoryId(null);
    setQuery("");
    // The content workspace is already mounted below the four space cards.
    // Move to it immediately; otherwise clicking a card looks like no action.
    contentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: bg }}
      data-testid="almond-reader-error">
      <div className="text-center max-w-lg">
        <h1 className="font-serif text-3xl text-[#39261B]">{error}</h1>
        <button type="button" onClick={() => nav("/programs")} className="mt-6 rounded-xl border border-[#D7C5AD] px-5 py-3 text-sm">
          Программы Anokina Studio
        </button>
      </div>
    </div>
  );

  if (!entry) return <div className="min-h-screen" style={{ background: bg }}
    data-testid="almond-reader-loading" aria-label="Загружается Том I" />;

  return (
    <div className="min-h-screen text-[#3A281E]" style={{ background: bg }}
      data-testid="almond-reader-single-contents">
      {!embedded && <header className="border-b border-[#DCCDB8]/80 bg-[#F8F1E6]/90">
        <div className="mx-auto max-w-7xl px-5 py-4 flex items-center gap-3">
          <button type="button" onClick={() => nav(returnToOwner ? ownerContentsPath : "/programs")}
            aria-label={returnToOwner ? "Вернуться к управлению Томом" : "Вернуться к программам"} className="rounded-full p-2 hover:bg-white/60">
            <ArrowLeft className="h-5 w-5" />
          </button>
          {entry.is_editor && (
            <button type="button"
              onClick={() => nav(returnToOwner ? ownerContentsPath : `/book/${encodeURIComponent(slug)}${ownerDraftPreview ? "?asReader=1" : ""}`)}
              className="ml-auto rounded-xl border border-[#D7C5AD] bg-white/50 px-3 py-2 text-xs text-[#644B36]"
              data-testid="almond-reader-owner-mode-switch">
              {returnToOwner ? "Вернуться к управлению Томом" : (ownerDraftPreview ? "Как читатель" : "Авторский просмотр")}
            </button>
          )}
        </div>
      </header>}

      <div className={`mx-auto max-w-7xl space-y-5 px-5 ${embedded ? "pt-0 pb-5" : "py-7"}`}>
        <TomeIAlmondHero />
        {!embedded && (
          <div className="flex justify-start sm:justify-end" data-testid="almond-reader-compact-search-container">
            <label className="flex h-10 w-full min-w-0 items-center gap-2 rounded-full border border-[#D7C5AD] bg-white/65 px-4 sm:max-w-[410px]">
              <Search className="h-4 w-4 shrink-0 text-[#8A6947]" aria-hidden="true" />
              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск по названию или рецептуре" aria-label="Поиск по Тому"
                className="min-w-0 flex-1 bg-transparent text-sm text-[#3A281E] outline-none placeholder:text-[#887565]"
                data-testid="almond-reader-search" />
            </label>
          </div>
        )}
        {(entry.intro_pages || []).length > 0 && (
          <section aria-label="Вступление">
            <div className="flex flex-wrap gap-2">
              {entry.intro_pages.map((p) => (
                <button key={p.id} type="button" onClick={() => openPage(p.id)}
                  className="rounded-xl border border-[#BDA78A] bg-white/50 px-4 py-3 text-sm text-left"
                  data-testid={`almond-reader-intro-${p.id}`}>
                  Вступление · {p.title} <ArrowRight className="ml-2 inline h-4 w-4" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section data-testid="almond-reader-four-spaces">
          <div className="grid gap-4 sm:grid-cols-2">
            {spaces.map((space, i) => {
              const Icon = space.icon;
              const section = space.section;
              const selected = activeSpace?.key === space.key;
              return (
                <button key={space.key} type="button"
                  onClick={() => openSpace(space.key)}
                  data-testid={`almond-reader-space-${space.key}`}
                  aria-pressed={!!selected}
                  className={`min-h-[170px] rounded-[24px] border p-6 text-left transition ${selected ? "border-[#A98B67] bg-[#EADCC8]" : "border-[#D7C5AD] bg-white/45 hover:bg-white/70"}`}>
                  <div className="flex items-start justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#AA8A68]/35 bg-[#EFE3D2] text-[#6B4E35]"><Icon className="h-5 w-5" /></span>
                    <span className="font-serif text-2xl text-[#A48768]">{space.number}</span>
                  </div>
                  <div className="mt-4 font-serif text-2xl">{space.title}</div>
                  <div className="mt-2 text-sm text-[#765F49]">{section?.subtitle || space.subtitle}</div>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs tracking-wide text-[#76563D]">
                    Открыть содержание <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section ref={contentsRef} data-testid="almond-reader-content-workspace">
          {query.trim() ? (
            <div className="mt-4 rounded-[24px] border border-[#D7C5AD] bg-white/55 p-6"
              data-testid="almond-reader-search-results">
              <h2 className="mb-4 font-serif text-2xl">Результаты поиска</h2>
              {searching && <p className="text-sm">Ищем…</p>}
              {!searching && searchResults?.length === 0 && <p className="text-sm">Материалы не найдены.</p>}
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {!searching && (searchResults || []).map((p) =>
                  <ReaderMaterialCard key={p.id} page={p} onOpen={openPage} />
                )}
              </div>
            </div>
          ) : activeSpace && (
            <div className="mt-4">
              <main className="rounded-[24px] border border-[#D7C5AD] bg-[#FAF5EC]/75 p-5 md:p-7">
                <h2 className="font-serif text-3xl">{activeSpace.title}</h2>
                {activeSpace.subtitle && <p className="mt-2 text-sm text-[#705945]">{activeSpace.subtitle}</p>}
                <div className="mt-6 grid gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
                  <div data-testid="almond-reader-categories">
                    {categories.map((category) => (
                      <button key={category.id} type="button"
                        onClick={() => setActiveCategoryId(category.id)}
                        aria-pressed={activeCategory?.id === category.id}
                        className={`mb-1 w-full rounded-xl px-3 py-3 text-left text-sm ${activeCategory?.id === category.id ? "bg-[#EADCC8]" : "hover:bg-white/60"}`}
                        data-testid={`almond-reader-category-${category.id}`}>
                        {category.title}
                      </button>
                    ))}
                    {!categories.length && <p className="px-3 py-3 text-sm text-[#765F49]">В этом пространстве пока нет категорий.</p>}
                  </div>
                  <div className="min-h-[230px] rounded-2xl border border-[#DDCDB7] bg-white/55 p-5">
                    <div className="mb-4 text-xs uppercase tracking-[0.15em] text-[#9A7A58]">
                      {activeCategory?.title || activeSpace.title}
                    </div>
                    {!!directPages.length && !!activeCategory && (
                      <div className="mb-4 grid gap-4 sm:grid-cols-2" data-testid="almond-reader-direct-pages">
                        {directPages.map(p => <ReaderMaterialCard key={p.id} page={p} onOpen={openPage} />)}
                      </div>
                    )}
                    {pages.length ? (
                      <div className="grid gap-4 sm:grid-cols-2" data-testid="almond-reader-material-cards">
                        {pages.map((p) => <ReaderMaterialCard key={p.id} page={p} onOpen={openPage} />)}
                      </div>
                    ) : (
                      <p className="py-10 text-center text-sm text-[#765F49]" data-testid="almond-reader-no-materials">
                        {ownerDraftPreview ? "Здесь пока нет материалов." : "Здесь пока нет опубликованных материалов."}
                      </p>
                    )}
                  </div>
                </div>
              </main>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ReaderMaterialCard({ page, onOpen }) {
  const thumbnail = useEditorialMediaUrl(page.thumbnail_url);
  return (
    <button type="button" onClick={() => onOpen(page.id)}
      className="group w-full overflow-hidden rounded-2xl border border-[#D8C5AB] bg-[#F5EBDD] text-left transition hover:bg-[#EFE2CD]"
      data-testid={`almond-reader-card-${page.id}`}>
      {/* Stable 4:5 cover: never crop Owner-approved editorial artwork. */}
      <div className="flex aspect-[4/5] w-full items-center justify-center overflow-hidden border-b border-[#D8C5AB] bg-[#EADBC5]/65"
        data-testid="almond-reader-card-cover">
        {thumbnail ? <img src={thumbnail} alt="" className="h-full w-full object-contain" /> : <BookOpen className="h-7 w-7 text-[#9B7D5B]/60" aria-hidden="true" />}
      </div>
      <div className="p-4">
        <h3 className="font-serif text-xl leading-snug">{page.title}</h3>
        {page.subtitle && <p className="mt-2 text-xs leading-relaxed text-[#765F49]">{page.subtitle}</p>}
        <span className="mt-3 flex items-center gap-1 text-xs text-[#76563D]">Открыть материал <ArrowRight className="h-3 w-3" /></span>
      </div>
    </button>
  );
}
